import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface ChartCardProps {
  title: string;
  subtitle?: string;
  height?: number;
  loading?: boolean;
  empty?: boolean;
  emptyMessage?: string;
  children: ReactNode;
}

/** Uniform card chrome (title/subtitle/fixed-height body) every dashboard chart renders inside —
 * the consistency here, not per-chart styling, is what makes the dashboard read as one designed
 * surface instead of a grab-bag of widgets. */
export default function ChartCard({ title, subtitle, height = 320, loading, empty, emptyMessage, children }: ChartCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack spacing={0.25} sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          ) : undefined}
        </Stack>

        <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {loading ? (
            <CircularProgress size={28} />
          ) : empty ? (
            <Typography variant="body2" color="text.secondary">
              {emptyMessage ?? 'No data available.'}
            </Typography>
          ) : (
            <Box sx={{ width: '100%', height: '100%' }}>{children}</Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
